import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { supabaseAvailable } from "../auth/AuthProvider";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Card, CardHeader } from "../ui/Card";
import { AuthLayout } from "./LoginPage";

const schema = z.object({
  fullName: z.string().min(2),
  email: z.string().email(),
  password: z.string().min(8),
});
type FormValues = z.infer<typeof schema>;

export function SignupPage() {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const { t } = useTranslation(["auth", "common"]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  async function onSubmit(values: FormValues) {
    setSubmitError(null);
    if (!supabase) {
      setSubmitError(t("supabaseNotConfigured"));
      return;
    }
    const { data, error } = await supabase.auth.signUp({
      email: values.email,
      password: values.password,
      options: { data: { full_name: values.fullName } },
    });
    if (error) {
      setSubmitError(error.message);
      return;
    }
    if (data.session) {
      navigate("/onboarding", { replace: true });
    } else {
      setNeedsConfirmation(true);
    }
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader title={t("signupTitle")} />
        {needsConfirmation ? (
          <p className="text-sm text-slate-700">{t("confirmation.body")}</p>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <Input
              label={t("fields.fullName")}
              autoComplete="name"
              disabled={!supabaseAvailable}
              error={errors.fullName ? t("common:validation.nameMin") : undefined}
              {...register("fullName")}
            />
            <Input
              label={t("fields.email")}
              type="email"
              autoComplete="email"
              disabled={!supabaseAvailable}
              error={errors.email ? t("common:validation.email") : undefined}
              {...register("email")}
            />
            <Input
              label={t("fields.password")}
              type="password"
              autoComplete="new-password"
              disabled={!supabaseAvailable}
              error={errors.password ? t("common:validation.passwordMin", { count: 8 }) : undefined}
              {...register("password")}
            />
            {submitError ? <p className="text-sm text-red-600">{submitError}</p> : null}
            <Button type="submit" loading={isSubmitting} disabled={!supabaseAvailable}>
              {t("buttons.signUp")}
            </Button>
            <p className="text-center text-sm text-slate-500">
              {t("links.haveAccount")}{" "}
              <Link to="/login" className="text-brand-700 hover:underline">
                {t("links.signIn")}
              </Link>
            </p>
          </form>
        )}
      </Card>
    </AuthLayout>
  );
}
